import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { 
  Bot, Target, ListOrdered, Heart, CheckSquare, ListTodo, FileText, Calendar, 
  Save, ArrowLeft, TrendingUp, Lightbulb, Brain, Sparkles, BarChart3, Clock,
  Zap, Flag, Trophy, RefreshCw, Compass, MessageSquare, Palette, Search
} from "lucide-react";

type DataSourceType = 'goals' | 'affirmations' | 'gratefuls' | 'habits' | 'tasks' | 'journal' | 'calendar';

interface ConstraintOption {
  key: string;
  label: string;
  description: string;
  icon: any;
}

interface DataSourceConfig {
  title: string;
  description: string;
  icon: any;
  constraints: ConstraintOption[];
}

const dataSourceConfigs: Record<DataSourceType, DataSourceConfig> = {
  goals: {
    title: "Goals",
    description: "Configure what AI insights to generate from your goals data",
    icon: Target,
    constraints: [
      { key: "progressInsights", label: "Progress Insights", description: "Analyze your goal progress and suggest next steps", icon: TrendingUp },
      { key: "milestoneRecommendations", label: "Milestone Recommendations", description: "Suggest optimal milestones to break down your goals", icon: Flag },
      { key: "prioritizationTips", label: "Goal Prioritization", description: "Help prioritize which goals to focus on", icon: ListOrdered },
      { key: "timelineAdjustments", label: "Timeline Adjustments", description: "Recommend deadline changes based on progress", icon: Clock },
      { key: "successStrategies", label: "Success Strategies", description: "Provide actionable strategies to achieve goals faster", icon: Trophy },
      { key: "obstacleIdentification", label: "Obstacle Identification", description: "Identify potential blockers and how to overcome them", icon: Search },
      { key: "goalAlignment", label: "Goal Alignment Analysis", description: "Check if your goals align with your values and vision", icon: Compass },
    ]
  },
  affirmations: {
    title: "Affirmations",
    description: "Configure what AI insights to generate from your affirmations",
    icon: ListOrdered,
    constraints: [
      { key: "personalizedSuggestions", label: "Personalized Suggestions", description: "Generate new affirmations tailored to your needs", icon: Sparkles },
      { key: "effectivenessAnalysis", label: "Effectiveness Analysis", description: "Analyze which affirmations resonate most with you", icon: BarChart3 },
      { key: "themeRecommendations", label: "Theme Recommendations", description: "Suggest affirmation themes based on your goals", icon: Palette },
      { key: "motivationBoosters", label: "Motivation Boosters", description: "Create powerful affirmations for challenging times", icon: Zap },
      { key: "mindsetShifts", label: "Mindset Shift Suggestions", description: "Identify limiting beliefs and suggest transformative affirmations", icon: Brain },
      { key: "dailyFocus", label: "Daily Focus Affirmations", description: "Recommend which affirmations to focus on each day", icon: Target },
    ]
  },
  gratefuls: {
    title: "Gratefuls",
    description: "Configure what AI insights to generate from your gratitude entries",
    icon: Heart,
    constraints: [
      { key: "patternInsights", label: "Gratitude Pattern Insights", description: "Identify recurring themes in what you're grateful for", icon: TrendingUp },
      { key: "moodCorrelation", label: "Mood Correlation Analysis", description: "Connect gratitude patterns to your emotional well-being", icon: Heart },
      { key: "expansionSuggestions", label: "Gratitude Expansion", description: "Suggest new areas of life to appreciate", icon: Sparkles },
      { key: "positivityTrends", label: "Positivity Trend Analysis", description: "Track how your gratitude practice evolves over time", icon: BarChart3 },
      { key: "appreciationReminders", label: "Appreciation Reminders", description: "Remind you of past gratitudes during challenging times", icon: RefreshCw },
      { key: "deeperReflection", label: "Deeper Reflection Prompts", description: "Encourage more meaningful gratitude exploration", icon: Brain },
    ]
  },
  habits: {
    title: "Habits",
    description: "Configure what AI insights to generate from your habit tracking",
    icon: CheckSquare,
    constraints: [
      { key: "streakAnalysis", label: "Streak Analysis", description: "Analyze your habit streaks and consistency patterns", icon: TrendingUp },
      { key: "stackingSuggestions", label: "Habit Stacking", description: "Suggest ways to link habits together for better success", icon: ListOrdered },
      { key: "consistencyTips", label: "Consistency Improvement", description: "Provide tips to maintain habits when motivation is low", icon: RefreshCw },
      { key: "goalAlignment", label: "Habit-Goal Alignment", description: "Show how habits connect to your larger goals", icon: Target },
      { key: "optimizationRecommendations", label: "Habit Optimization", description: "Suggest timing and frequency adjustments", icon: Clock },
      { key: "triggerIdentification", label: "Trigger Identification", description: "Help identify cues that support or hinder habits", icon: Zap },
      { key: "progressCelebration", label: "Progress Celebration", description: "Acknowledge and celebrate your habit wins", icon: Trophy },
    ]
  },
  tasks: {
    title: "Tasks",
    description: "Configure what AI insights to generate from your tasks",
    icon: ListTodo,
    constraints: [
      { key: "prioritizationSuggestions", label: "Task Prioritization", description: "Help prioritize tasks based on importance and urgency", icon: Flag },
      { key: "productivityPatterns", label: "Productivity Patterns", description: "Identify when you're most productive for different task types", icon: BarChart3 },
      { key: "timeEstimates", label: "Time Management Insights", description: "Improve task time estimates based on history", icon: Clock },
      { key: "completionPredictions", label: "Completion Predictions", description: "Predict task completion likelihood and suggest adjustments", icon: TrendingUp },
      { key: "workloadBalance", label: "Workload Balance", description: "Analyze if you're overcommitting and suggest balance", icon: Compass },
      { key: "focusRecommendations", label: "Focus Recommendations", description: "Suggest which tasks to tackle first each day", icon: Target },
      { key: "delegationSuggestions", label: "Delegation Suggestions", description: "Identify tasks that could be delegated or simplified", icon: Lightbulb },
    ]
  },
  journal: {
    title: "Journal",
    description: "Configure what AI insights to generate from your journal entries",
    icon: FileText,
    constraints: [
      { key: "emotionalPatterns", label: "Emotional Pattern Analysis", description: "Track emotional themes and patterns over time", icon: Heart },
      { key: "themeExtraction", label: "Theme Extraction", description: "Identify recurring topics and concerns in your writing", icon: Search },
      { key: "reflectionPrompts", label: "Reflection Prompts", description: "Generate personalized prompts based on your entries", icon: MessageSquare },
      { key: "growthTracking", label: "Personal Growth Tracking", description: "Highlight evidence of growth and positive change", icon: TrendingUp },
      { key: "insightSummaries", label: "Insight Summaries", description: "Summarize key insights from your journal over time", icon: Lightbulb },
      { key: "connectionFinding", label: "Connection Finding", description: "Find connections between journal entries and life events", icon: Compass },
      { key: "writingEnhancement", label: "Writing Enhancement Tips", description: "Suggest ways to deepen your journaling practice", icon: Sparkles },
    ]
  },
  calendar: {
    title: "Calendar",
    description: "Configure what AI insights to generate from your calendar",
    icon: Calendar,
    constraints: [
      { key: "scheduleOptimization", label: "Schedule Optimization", description: "Suggest better ways to organize your time", icon: Clock },
      { key: "timeAllocation", label: "Time Allocation Insights", description: "Analyze how you spend time across different activities", icon: BarChart3 },
      { key: "meetingEfficiency", label: "Meeting Efficiency Tips", description: "Improve meeting scheduling and reduce time waste", icon: Zap },
      { key: "workLifeBalance", label: "Work-Life Balance Analysis", description: "Track balance between work, personal, and rest time", icon: Compass },
      { key: "conflictPrevention", label: "Conflict Prevention", description: "Identify scheduling conflicts before they happen", icon: Search },
      { key: "energyManagement", label: "Energy Management", description: "Align demanding tasks with your peak energy times", icon: TrendingUp },
      { key: "bufferRecommendations", label: "Buffer Recommendations", description: "Suggest breaks and buffer time between activities", icon: RefreshCw },
    ]
  }
};

interface AICoachConstraintsPageProps {
  dataSource: DataSourceType;
}

export default function AICoachConstraintsPage({ dataSource }: AICoachConstraintsPageProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  
  const config = dataSourceConfigs[dataSource];
  const storageKey = `ai-coach-constraints-${dataSource}`;
  
  const [constraints, setConstraints] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults: Record<string, boolean> = {};
    config.constraints.forEach(c => {
      defaults[c.key] = true;
    });
    return defaults;
  });

  const handleToggle = (key: string) => {
    setConstraints(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = () => {
    localStorage.setItem(storageKey, JSON.stringify(constraints));
    toast({
      title: "Constraints saved",
      description: `Your ${config.title} AI constraints have been updated.`,
    });
  };

  const handleBack = () => {
    setLocation("/ai-coach/settings");
  };

  const Icon = config.icon;

  return (
    <div className="w-full max-w-full p-4 md:p-6">
      <Button 
        variant="ghost" 
        onClick={handleBack} 
        className="mb-4 -ml-2"
        data-testid="button-back-to-settings"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Settings
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" data-testid={`text-constraints-title-${dataSource}`}>
            {config.title} AI Constraints
          </h1>
          <p className="text-muted-foreground">{config.description}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            AI Insight Types
          </CardTitle>
          <CardDescription>
            Toggle which types of AI-generated insights you want to receive based on your {config.title.toLowerCase()} data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {config.constraints.map((constraint) => {
            const ConstraintIcon = constraint.icon;
            const isEnabled = constraints[constraint.key] ?? true;
            return (
              <div key={constraint.key} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-muted rounded-md flex items-center justify-center">
                    <ConstraintIcon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label htmlFor={constraint.key} className="text-base font-medium cursor-pointer">
                      {constraint.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">{constraint.description}</p>
                  </div>
                </div>
                <Switch
                  id={constraint.key}
                  checked={isEnabled}
                  onCheckedChange={() => handleToggle(constraint.key)}
                  data-testid={`switch-constraint-${constraint.key}`}
                />
              </div>
            );
          })}

          <div className="pt-4 border-t">
            <Button onClick={handleSave} className="w-full" data-testid="button-save-constraints">
              <Save className="w-4 h-4 mr-2" />
              Save Constraints
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
